import { ReactNode } from 'react'

import { Currency, Price } from '@cowprotocol/currency'
import { Command } from '@cowprotocol/types'

import { Nullish } from 'types'

import { OrderStatus } from 'legacy/state/orders/actions'

import { PENDING_EXECUTION_THRESHOLD_PERCENTAGE } from 'common/constants/common'
import { PriceDifference } from 'utils/orderUtils/calculatePriceDifference'
import { getIsFinalizedOrder } from 'utils/orderUtils/getIsFinalizedOrder'
import { ParsedOrder } from 'utils/orderUtils/parseOrder'

import * as styledEl from '../../containers/OrderRow/OrderRow.styled'
import { getDistanceColor } from '../../utils/getDistanceColor'
import { WarningReason } from '../OrderEstimatedExecutionPrice/orderEstimatedExecutionPrice.constants'
import { OrderEstimatedExecutionPrice } from '../OrderEstimatedExecutionPrice/OrderEstimatedExecutionPrice.pure'
import { TwapOrderStatus } from '../TwapOrderStatus/TwapOrderStatus.pure'
import { TwapScheduledOrderStatus } from '../TwapScheduledOrderStatus/TwapScheduledOrderStatus.pure'

export interface OrderFillsAtWithDistanceProps {
  isTwapTable?: boolean
  isChild?: boolean
  childOrders?: ParsedOrder[]
  estimatedPriceWarning: ReactNode | undefined
  estimatedExecutionPrice: Nullish<Price<Currency, Currency>>
  spotPrice: Nullish<Price<Currency, Currency>>
  order: ParsedOrder
  isInverted: boolean
  isUnfillable: boolean
  isFallbackHandlerRequired?: boolean
  withWarning: boolean
  warningReason?: WarningReason
  onApprove?: Command
  priceDiffs: PriceDifference
  orderFillsAt: ReactNode
}

// TODO: Break down this large function into smaller functions
// TODO: Add proper return type annotation
// TODO: Reduce function complexity by extracting logic
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function OrderFillsAtWithDistance({
  isTwapTable,
  isChild,
  childOrders,
  estimatedPriceWarning,
  estimatedExecutionPrice,
  spotPrice,
  order,
  isInverted,
  isUnfillable,
  isFallbackHandlerRequired,
  withWarning,
  warningReason,
  onApprove,
  priceDiffs,
  orderFillsAt,
}: OrderFillsAtWithDistanceProps) {
  // For TWAP parent orders
  if (isTwapTable && !isChild && childOrders) {
    return (
      estimatedPriceWarning || (
        <styledEl.CellElement doubleRow>
          <TwapOrderStatus
            orderStatus={order.status}
            childOrders={childOrders}
            isFallbackHandlerRequired={isFallbackHandlerRequired}
          >
            <TwapScheduledOrderStatus
              childOrders={childOrders}
              isInverted={isInverted}
              isUnfillable={isUnfillable}
              estimatedExecutionPrice={estimatedExecutionPrice}
              spotPrice={spotPrice}
            />
          </TwapOrderStatus>
        </styledEl.CellElement>
      )
    )
  }

  // Regular order display logic (including child orders)
  if (withWarning || isUnfillable) {
    return (
      <styledEl.ExecuteCellWrapper>
        <OrderEstimatedExecutionPrice
          amount={undefined}
          tokenSymbol={undefined}
          isInverted={isInverted}
          isUnfillable={true}
          canShowWarning={true}
          warningReason={warningReason}
          onApprove={onApprove}
        />
      </styledEl.ExecuteCellWrapper>
    )
  }

  // Regular order display logic
  const distance =
    getIsFinalizedOrder(order) ||
    order.status === OrderStatus.CANCELLED ||
    isUnfillable ||
    (priceDiffs?.percentage &&
      Math.abs(Number(priceDiffs.percentage.toFixed(4))) <= PENDING_EXECUTION_THRESHOLD_PERCENTAGE)
      ? ''
      : priceDiffs?.percentage
        ? `${priceDiffs?.percentage.toFixed(2)}%`
        : '-'

  return (
    <styledEl.CellElement doubleRow>
      <b>{orderFillsAt}</b>
      <i
        style={{
          color:
            !isUnfillable && priceDiffs?.percentage
              ? getDistanceColor(Number(priceDiffs.percentage.toFixed(4)))
              : 'inherit',
        }}
      >
        {distance}
      </i>
    </styledEl.CellElement>
  )
}
